import { Box, Button, CheckIcon, Flex, Typography } from '@iheartjane/reefer';

import { RatingStars } from '../../ratingStars/ratingStars';
import type { ReviewableProduct } from '../../reviewableProduct';
import { RateAndReviewModal } from '../rateAndReviewModal/rateAndReviewModal';
import { ReviewProductHeader } from '../reviewProductHeader/reviewProductHeader';

import styles from './reviewSubmitted.module.css';

export interface ReviewSubmittedProps {
  /** Other products to review, listed below the confirmation. */
  moreToReview?: ReviewableProduct[];
  /** Footer "I'm done" action. */
  onDone: () => void;
  onReviewProduct: (product: ReviewableProduct) => void;
  productName: string;
  /** Whole-star rating the user gave (1-5). */
  rating: number;
  testId?: string;
}

/**
 * Final Rate & Review phase, shared by every A/B test variant.
 * Renders inside `RateAndReviewModal` and suggests other products to review.
 */
export function ReviewSubmitted({
  moreToReview = [],
  onDone,
  onReviewProduct,
  productName,
  rating,
  testId = 'review-submitted',
}: ReviewSubmittedProps) {
  return (
    <>
      <RateAndReviewModal.Body>
        <Flex
          alignItems="center"
          data-testid={testId}
          flexDirection="column"
          width="100%"
        >
          <Flex
            alignItems="center"
            justifyContent="center"
            background="success-light"
            className={styles['successIcon']}
            height={64}
            width={64}
          >
            <CheckIcon color="success" size="lg" />
          </Flex>
          <Typography
            as="h2"
            branded
            mt={8}
            textAlign="center"
            variant="h2-semibold"
          >
            Review submitted
          </Typography>
          <Typography color="grays-mid" mt={8} textAlign="center">
            Thank you for helping your community by reviewing {productName}
          </Typography>
          <Flex mt={8}>
            <RatingStars rating={rating} size="lg" />
          </Flex>
          {moreToReview.length > 0 && (
            <Flex flexDirection="column" gap={16} mt={24} width="100%">
              <Typography
                as="h3"
                color="grays-mid"
                textAlign="center"
                variant="caps-semibold"
              >
                {moreToReview.length} more to review
              </Typography>
              <ul
                className={styles['list']}
                data-testid={`${testId}-more-to-review`}
              >
                {moreToReview.map((product) => (
                  <li key={product.id}>
                    <Box
                      border="grays-light"
                      borderRadius="lg"
                      data-testid={`${testId}-product-${product.id}`}
                      p={16}
                    >
                      <Flex
                        alignItems="center"
                        gap={16}
                        justifyContent="space-between"
                      >
                        <ReviewProductHeader
                          brand={product.brand}
                          category={product.category}
                          imageUrl={product.imageUrl}
                          name={product.name}
                          nameMaxLines={2}
                          size="compact"
                          testId={`${testId}-product-${product.id}-header`}
                        />
                        <Button
                          ariaLabel={`Review ${product.name}`}
                          className={styles['reviewButton']}
                          label="Review"
                          onClick={() => onReviewProduct(product)}
                          size="small"
                        />
                      </Flex>
                    </Box>
                  </li>
                ))}
              </ul>
            </Flex>
          )}
        </Flex>
      </RateAndReviewModal.Body>
      <RateAndReviewModal.Footer>
        <Button label="I'm done" onClick={onDone} variant="tertiary" />
      </RateAndReviewModal.Footer>
    </>
  );
}
